/// <reference types="gtag.js" />

import {setCookie} from './manage-cookies.mts';
import {conversionHit, retargetingHit, SeznameConversionHit} from './manage-seznam.mts';
import {fbq} from './manage-fbq.mts';

const getRequiredElement = <T extends HTMLElement>(selector: string): T => {
	const element = document.querySelector<T>(selector);
	if (element === null)
	{
		throw new TypeError(`Missing required element: ${selector}`);
	}

	return element;
};

const cookiesContainer = getRequiredElement<HTMLElement>('#cookies-container');

export type CookiePreference = 'necessary' | 'analytical' | 'marketing' | 'other';
export const cookiePreferences: {
	[key in CookiePreference]: boolean | undefined
} = {
	necessary: true,
	analytical: undefined,
	marketing: undefined,
	other: undefined,
};

const seznamIdentifiers: {
	sklikId?: number,
	retargetingId?: number,
} = {};

type GooglePurchaseItem = {
	readonly item_id: string;
	readonly item_name: string;
	readonly item_category: string;
	readonly quantity: number;
};

type GoogleUserData = {
	readonly sha256_email_address: string;
	readonly sha256_phone_number?: string;
};

type GooglePurchase = {
	readonly transaction_id: string;
	readonly value: number;
	readonly currency: string;
	readonly items: readonly GooglePurchaseItem[];
	readonly user_data?: GoogleUserData;
};

const closePopup = () => {
	cookiesContainer.style.display = 'none';
};
const showPopup = () => {
	cookiesContainer.style.display = 'block';
	window.scrollTo(0, 0);
};
const switchToDetailed = () => {
	const cookieSimple = getRequiredElement<HTMLElement>('#cookie-simple');
	const cookieDetailed = getRequiredElement<HTMLElement>('#cookie-detailed');

	cookieSimple.style.display = 'none';
	cookieDetailed.style.display = 'block';
};

const triggerGtag = () => gtag('consent', 'update', {
	ad_user_data: cookiePreferences.marketing ? 'granted' : 'denied',
	ad_personalization: cookiePreferences.marketing ? 'granted' : 'denied',
	ad_storage: cookiePreferences.marketing ? 'granted' : 'denied',
	analytics_storage: cookiePreferences.analytical ? 'granted' : 'denied',
});

const triggerGooglePurchase = (purchase: GooglePurchase) => {
	if (!cookiePreferences.analytical)
	{
		return;
	}

	const {user_data: userData, ...purchaseEvent} = purchase;
	if (cookiePreferences.marketing && userData !== undefined)
	{
		gtag('set', 'user_data', userData);
	}

	gtag('event', 'purchase', purchaseEvent);
};

const triggerFbq = () => fbq('consent', cookiePreferences.analytical ? 'grant' : 'revoke');

const triggerSeznamConversion = (args: Partial<SeznameConversionHit>) => {
	if (seznamIdentifiers.sklikId === undefined)
	{
		return;
	}

	conversionHit({
		id: seznamIdentifiers.sklikId,
		consent: cookiePreferences.analytical ? 1 : 0,
		... args
	});
};
const triggerSeznamRetargeting = () => {
	if (seznamIdentifiers.retargetingId === undefined)
	{
		return;
	}

	retargetingHit({
		rtgId: seznamIdentifiers.retargetingId,
		consent: cookiePreferences.marketing ? 1 : 0,
	});
};

const persistToCookies = () => {
	setCookie('COOKIES_consent', '1', {days: 365});
	setCookie('COOKIES_analytical', cookiePreferences.analytical ? '1' : '0', {days: 365});
	setCookie('COOKIES_marketing', cookiePreferences.marketing ? '1' : '0', {days: 365});
	setCookie('COOKIES_other', cookiePreferences.other ? '1' : '0', {days: 365});

	triggerGtag();
	triggerFbq();
	triggerSeznamRetargeting();
}

const approveCookieClick = (e: Event) => {
	e.preventDefault();

	cookiePreferences.analytical = true;
	cookiePreferences.marketing = true;
	cookiePreferences.other = true;

	persistToCookies();

	closePopup();
}

const denyCookieClick = (e: Event) => {
	e.preventDefault();

	cookiePreferences.analytical = false;
	cookiePreferences.marketing = false;
	cookiePreferences.other = false;

	persistToCookies();

	closePopup();
}

const detailedCookieClick = (e: Event) => {
	e.preventDefault();

	switchToDetailed();
}

const openCookieSettingsClick = (e: Event) => {
	e.preventDefault();

	switchToDetailed();
	showPopup();
}

const saveCookieClick = (e: Event) => {
	e.preventDefault();

	const cookieAnalyticalOption = getRequiredElement<HTMLInputElement>('#cookie-analytical');
	const cookieMarketingOption = getRequiredElement<HTMLInputElement>('#cookie-marketing');
	const cookieOtherOption = getRequiredElement<HTMLInputElement>('#cookie-other');

	cookiePreferences.analytical = cookieAnalyticalOption.checked;
	cookiePreferences.marketing = cookieMarketingOption.checked;
	cookiePreferences.other = cookieOtherOption.checked;

	persistToCookies();

	closePopup();
}

const init = (arg: {
	hasUserConsent: boolean;
	analyticsConsent: boolean;
	marketingConsent: boolean;
	otherConsent: boolean;
	seznamSklikId: number;
	seznamRetargetingId: number;
	googleAnalytics: string;
	googleTagManager: string;
	facebookAppId: string;
}) => {
	cookiePreferences.analytical = arg.analyticsConsent;
	cookiePreferences.marketing = arg.marketingConsent;
	cookiePreferences.other = arg.otherConsent;

	seznamIdentifiers.sklikId = arg.seznamSklikId;
	seznamIdentifiers.retargetingId = arg.seznamRetargetingId;

	gtag('js', new Date());
	gtag('config', arg.googleTagManager);

	triggerGtag();
	triggerSeznamRetargeting();
};

document.querySelectorAll('.open-cookie-settings').forEach((userItem) => {
	userItem.addEventListener('click', openCookieSettingsClick)
});

const approveCookieButton = getRequiredElement<HTMLButtonElement>('#button-cookie-approve');
approveCookieButton.addEventListener('click', approveCookieClick);

const denyCookieButton = getRequiredElement<HTMLButtonElement>('#button-cookie-deny');
denyCookieButton.addEventListener('click', denyCookieClick);

const detailedCookieButton = getRequiredElement<HTMLButtonElement>('#button-cookie-detailed');
detailedCookieButton.addEventListener('click', detailedCookieClick);

const saveCookieButton = getRequiredElement<HTMLButtonElement>('#button-cookie-save');
saveCookieButton.addEventListener('click', saveCookieClick);

const cookieInitJson = getRequiredElement<HTMLScriptElement>('#cookie-init');

init(JSON.parse(cookieInitJson.textContent));

const googlePurchase = document.getElementById('google-purchase');
if (googlePurchase !== null)
{
	triggerGooglePurchase(JSON.parse(googlePurchase.textContent));
}

const seznamConversionHit = document.getElementById('seznam-conversion-hit');

if (seznamConversionHit !== null && seznamConversionHit !== undefined)
{
	triggerSeznamConversion(JSON.parse(seznamConversionHit.textContent));
}
